import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { GitMerge, ArrowRight, AlertTriangle, Search } from "lucide-react";

const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`);

const CONFLICT_TONE = {
  phone_mismatch: "warn", email_mismatch: "warn",
  duplicate_product_opportunity: "warn",
  both_have_cases: "warn", has_active_case: "info",
  both_have_payment_plans: "warn", active_payment_plan: "info",
  source_has_payments: "info", target_has_payments: "info",
};

function ConflictRow({ c }) {
  const tone = CONFLICT_TONE[c.type] || "warn";
  return (
    <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
      tone === "warn" ? "border-amber-500/40 bg-amber-500/10" : "border-[hsl(var(--info))]/30 bg-[hsl(var(--info))]/5"}`}>
      <AlertTriangle className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${tone === "warn" ? "text-amber-600 dark:text-amber-400" : "text-[hsl(var(--info))]"}`} />
      <span>{c.message}</span>
    </div>
  );
}

function DomainSummary({ label, side }) {
  return (
    <div className="flex-1 min-w-56 space-y-2 rounded-md border p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
      <div>
        <div className="font-medium leading-tight">{side.full_name}</div>
        <div className="text-xs text-muted-foreground font-mono">{side.customer_id}</div>
        <div className="text-xs text-muted-foreground">{[side.primary_phone, side.email].filter(Boolean).join(" · ")}</div>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs tabular-nums pt-1 border-t">
        <span className="text-muted-foreground">Opportunities</span><span className="font-medium text-right">{side.opportunities.length}</span>
        <span className="text-muted-foreground">Consultations</span><span className="font-medium text-right">{side.consultations.length}</span>
        <span className="text-muted-foreground">Calls</span><span className="font-medium text-right">{side.call_history_count}</span>
        <span className="text-muted-foreground">Payment plans</span><span className="font-medium text-right">{side.finance.plans}</span>
        <span className="text-muted-foreground">Total paid</span><span className="font-medium text-right">{inr(side.finance.total_paid)}</span>
        <span className="text-muted-foreground">Process cases</span><span className="font-medium text-right">{side.process_cases.length}</span>
        <span className="text-muted-foreground">Open tasks</span><span className="font-medium text-right">{side.open_tasks}</span>
        <span className="text-muted-foreground">Tickets</span><span className="font-medium text-right">{side.tickets.length}</span>
        <span className="text-muted-foreground">Lead intakes</span><span className="font-medium text-right">{side.lead_intakes.length}</span>
      </div>
      {side.opportunities.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {side.opportunities.map((o) => (
            <Badge key={o.opportunity_id} variant="outline" className="text-[10px] font-normal">
              {o.product_code} · {o.stage || o.lifecycle_status}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

/** Shared rich preview — used both by the requester's own confirm step and
 * by Leadership's Approve/Reject dialog on ApprovalsPage, so what Leadership
 * reviews is exactly what the requester saw, never a summarized version. */
export function MergePreviewCard({ preview }) {
  if (!preview) return null;
  return (
    <div className="space-y-3" data-testid="merge-preview">
      <div className="flex items-stretch gap-2">
        <DomainSummary label="Merged away (becomes a redirect)" side={preview.source} />
        <div className="flex items-center shrink-0"><ArrowRight className="h-4 w-4 text-muted-foreground" /></div>
        <DomainSummary label="Surviving record" side={preview.target} />
      </div>
      {!preview.identity_match.phone && !preview.identity_match.email && (
        <div className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
          <span>Neither phone nor email matches between these two records — confirm this is genuinely the same person before requesting a merge.</span>
        </div>
      )}
      {preview.conflicts.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-muted-foreground">Things to review before approving</div>
          {preview.conflicts.map((c, i) => <ConflictRow key={i} c={c} />)}
        </div>
      )}
      {preview.conflicts.length === 0 && (
        <div className="text-xs text-muted-foreground">No conflicts detected — records look like a clean duplicate.</div>
      )}
    </div>
  );
}

/** Entry point from Customer 360: search -> preview -> reason -> request.
 * Never merges directly — always ends in a Leadership approval request. */
export function MergeCustomerAction({ contact }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [target, setTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pickerOpen) return;
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/contacts/merge-candidates?q=${encodeURIComponent(q)}&exclude_contact_id=${contact.contact_id}`)
        .then((r) => setResults(r.data)).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, pickerOpen, contact.contact_id]);

  const pick = async (candidate) => {
    setPickerOpen(false);
    setTarget(candidate);
    try {
      const { data } = await api.post(`/contacts/${contact.contact_id}/merge-preview`,
        { target_contact_id: candidate.contact_id });
      setPreview(data);
    } catch (e) {
      toast.error(errDetail(e));
      setTarget(null);
    }
  };

  const close = () => { setTarget(null); setPreview(null); setReason(""); };

  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/contacts/${contact.contact_id}/merge-request`,
        { target_contact_id: target.contact_id, reason: reason.trim() });
      toast.success("Merge requested — sent to Leadership for approval");
      close();
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" data-testid="merge-customer-btn" onClick={() => setPickerOpen(true)}>
        <GitMerge className="h-3.5 w-3.5 mr-1.5" />Merge into…
      </Button>

      <CommandDialog open={pickerOpen} onOpenChange={setPickerOpen} title="Merge target search"
                    description="Search for the customer to merge this record into, by name, phone, email or Customer ID.">
        <CommandInput data-testid="merge-search-input" placeholder="Search the customer to merge this record into…" value={q} onValueChange={setQ} />
        <CommandList>
          <CommandEmpty>{q.trim().length < 2 ? "Type at least 2 characters…" : "No matching customers."}</CommandEmpty>
          {results.length > 0 && (
            <CommandGroup heading="Candidates">
              {results.map((r) => (
                <CommandItem key={r.contact_id} data-testid={`merge-candidate-${r.customer_id}`}
                  value={`${r.full_name} ${r.customer_id} ${r.primary_phone || ""}`}
                  onSelect={() => pick(r)}>
                  <Search className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                  <span className="font-medium">{r.full_name}</span>
                  <span className="ml-2 text-xs text-muted-foreground font-mono">{r.customer_id}</span>
                  {r.primary_phone && <span className="ml-2 text-xs text-muted-foreground">{r.primary_phone}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>

      <Dialog open={!!target} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-2xl" data-testid="merge-preview-dialog">
          <DialogHeader>
            <DialogTitle>Request merge — {contact.full_name} into {target?.full_name}</DialogTitle>
            <DialogDescription>
              Nothing is merged yet. This sends a request to Leadership with the preview below; the merge only
              happens if they approve it, and the record being merged away is always kept (marked "merged", never deleted).
            </DialogDescription>
          </DialogHeader>
          <MergePreviewCard preview={preview} />
          <div className="space-y-1">
            <Label className="text-xs">Reason for this merge (required)</Label>
            <Textarea data-testid="merge-reason" placeholder="e.g. Same person, submitted twice — same phone number, confirmed by phone." value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button data-testid="merge-request-submit" disabled={busy || !preview} onClick={submit}>Send to Leadership</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
